import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, WifiOff, Gift } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useOfflineAuth } from '../../hooks/useOfflineAuth';
import { Button } from '../common/Button';
import { CompactOfflineIndicator } from '../common/OfflineIndicator';
import { MonitoringStatusWidget } from '../monitoring/MonitoringStatusWidget';
import { UserFeedbackCollector } from '../monitoring/UserFeedbackCollector';
import { FEATURES } from '../../config/features';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuthStore();
  const { isOnline, canAccessReadingHistory } = useOfflineAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMenuOpen(false);
  };

  return (
    <header className="bg-dark-800 border-b border-dark-700">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/app" 
            className="flex items-center space-x-2 text-white hover:text-primary-500 transition-colors"
          >
            <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">🎴</span>
            </div>
            <span className="font-bold text-lg">Tarot Reader</span>
          </Link>

          {/* Desktop Navigation */}
          {isAuthenticated && (
            <nav className="hidden md:flex items-center space-x-6">
              <Link 
                to="/app" 
                className="text-gray-300 hover:text-white transition-colors"
              >
                Dashboard
              </Link>
              
              {/* Reading - Requires Online */}
              {isOnline ? (
                <Link 
                  to="/reading" 
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Get Reading
                </Link>
              ) : (
                <span 
                  className="text-gray-500 cursor-not-allowed flex items-center gap-1"
                  title="Requires internet connection"
                >
                  Get Reading
                  <WifiOff className="h-3 w-3" />
                </span>
              )}
              
              {/* History - Works Offline */}
              <Link 
                to="/history" 
                className={`transition-colors ${
                  canAccessReadingHistory 
                    ? 'text-gray-300 hover:text-white' 
                    : 'text-gray-500 cursor-not-allowed'
                }`}
                onClick={(e) => {
                  if (!canAccessReadingHistory) {
                    e.preventDefault();
                  }
                }}
                title={!canAccessReadingHistory ? 'Requires valid authentication' : ''}
              >
                History
                {!isOnline && canAccessReadingHistory && (
                  <span className="ml-1 text-xs text-blue-400">(Cached)</span>
                )}
              </Link>
              
              {/* Shop - Requires Online */}
              {isOnline ? (
                <Link 
                  to="/shop" 
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Shop
                </Link>
              ) : (
                <span 
                  className="text-gray-500 cursor-not-allowed flex items-center gap-1"
                  title="Requires internet connection"
                >
                  Shop
                  <WifiOff className="h-3 w-3" />
                </span>
              )}
              
              {/* Inventory - Requires Online */}
              {isOnline ? (
                <Link 
                  to="/inventory" 
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Inventory
                </Link>
              ) : (
                <span 
                  className="text-gray-500 cursor-not-allowed flex items-center gap-1"
                  title="Requires internet connection"
                >
                  Inventory
                  <WifiOff className="h-3 w-3" />
                </span>
              )}
              
              {/* Rewards - Requires Online */}
              {isOnline ? (
                <Link 
                  to="/rewards" 
                  className="text-gray-300 hover:text-white transition-colors flex items-center gap-1"
                >
                  <Gift className="h-4 w-4" />
                  Rewards
                </Link>
              ) : (
                <span 
                  className="text-gray-500 cursor-not-allowed flex items-center gap-1"
                  title="Requires internet connection"
                >
                  <Gift className="h-4 w-4" />
                  Rewards
                  <WifiOff className="h-3 w-3" />
                </span>
              )}
              
              {/* Profile - Works Offline */}
              <Link 
                to="/profile" 
                className={`transition-colors ${
                  canAccessReadingHistory 
                    ? 'text-gray-300 hover:text-white' 
                    : 'text-gray-500 cursor-not-allowed'
                }`}
                onClick={(e) => {
                  if (!canAccessReadingHistory) {
                    e.preventDefault();
                  }
                }}
                title={!canAccessReadingHistory ? 'Requires valid authentication' : ''}
              >
                Profile
              </Link>
            </nav>
          )}

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {/* Offline Indicator */}
            {isAuthenticated && <CompactOfflineIndicator className="hidden md:flex" />}
            
            {/* Development Monitoring Widget */}
            {import.meta.env.DEV && isAuthenticated && (
              <MonitoringStatusWidget 
                compact={true}
                showFeedbackButton={false}
                className="hidden lg:block"
              />
            )}
            
            {isAuthenticated ? (
              <>
                {/* Desktop User Menu with Avatar */}
                <div className="flex items-center space-x-3 max-md:hidden">
                  {/* Avatar - 48x48 */}
                  <Link 
                    to="/profile"
                    className="flex-shrink-0 hover:opacity-80 transition-opacity"
                    title="View Profile"
                  >
                    {user?.avatarUrl ? (
                      <img
                        src={user.avatarUrl.startsWith('http') ? user.avatarUrl : `https://www.tarot-app.online${user.avatarUrl}`}
                        alt="Profile"
                        className="w-12 h-12 rounded-full object-cover border-2 border-primary-500"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white text-lg font-bold border-2 border-primary-500">
                        {(user?.displayName || user?.nickname || user?.email)?.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </Link>
                  
                  <div className="flex items-center space-x-2 text-gray-300">
                    <span className="text-sm">{user?.displayName || user?.nickname || user?.email}</span>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="text-gray-300 hover:text-white"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>

                {/* Mobile Menu Button */}
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="md:hidden text-gray-300 hover:text-white"
                >
                  {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button variant="primary" size="sm">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && isAuthenticated && (
          <div className="md:hidden py-4 border-t border-dark-700">
            {/* Offline Status in Mobile Menu */}
            {!isOnline && (
              <div className="px-4 py-2 mb-2 bg-blue-900/20 border border-blue-700 rounded text-sm text-blue-300">
                <div className="flex items-center gap-2">
                  <WifiOff className="h-4 w-4" />
                  <span>Offline Mode - Limited Features</span>
                </div>
              </div>
            )}
            
            <nav className="flex flex-col space-y-2">
              <Link 
                to="/app" 
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-dark-700 rounded transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Dashboard
              </Link>
              
              {/* Reading - Requires Online */}
              {isOnline ? (
                <Link 
                  to="/reading" 
                  className="px-4 py-2 text-gray-300 hover:text-white hover:bg-dark-700 rounded transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get Reading
                </Link>
              ) : (
                <div className="px-4 py-2 text-gray-500 flex items-center justify-between">
                  <span>Get Reading</span>
                  <span className="text-xs flex items-center gap-1">
                    <WifiOff className="h-3 w-3" />
                    Offline
                  </span>
                </div>
              )}
              
              {/* History - Works Offline */}
              <Link 
                to="/history" 
                className={`px-4 py-2 rounded transition-colors ${
                  canAccessReadingHistory 
                    ? 'text-gray-300 hover:text-white hover:bg-dark-700' 
                    : 'text-gray-500 cursor-not-allowed'
                }`}
                onClick={(e) => {
                  if (!canAccessReadingHistory) {
                    e.preventDefault();
                  } else {
                    setIsMenuOpen(false);
                  }
                }}
              >
                <div className="flex items-center justify-between">
                  <span>History</span>
                  {!isOnline && canAccessReadingHistory && (
                    <span className="text-xs text-blue-400">Cached</span>
                  )}
                </div>
              </Link>
              
              {/* Shop - Requires Online */}
              {isOnline ? (
                <Link 
                  to="/shop" 
                  className="px-4 py-2 text-gray-300 hover:text-white hover:bg-dark-700 rounded transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Shop
                </Link>
              ) : (
                <div className="px-4 py-2 text-gray-500 flex items-center justify-between">
                  <span>Shop</span>
                  <span className="text-xs flex items-center gap-1">
                    <WifiOff className="h-3 w-3" />
                    Offline
                  </span>
                </div>
              )}
              
              {/* Inventory - Requires Online */}
              {isOnline ? (
                <Link 
                  to="/inventory" 
                  className="px-4 py-2 text-gray-300 hover:text-white hover:bg-dark-700 rounded transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Inventory
                </Link>
              ) : (
                <div className="px-4 py-2 text-gray-500 flex items-center justify-between">
                  <span>Inventory</span>
                  <span className="text-xs flex items-center gap-1">
                    <WifiOff className="h-3 w-3" />
                    Offline
                  </span>
                </div>
              )}
              
              {/* Rewards - Requires Online */}
              {isOnline ? (
                <Link 
                  to="/rewards" 
                  className="px-4 py-2 text-gray-300 hover:text-white hover:bg-dark-700 rounded transition-colors flex items-center gap-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Gift className="h-4 w-4" />
                  Rewards
                </Link>
              ) : (
                <div className="px-4 py-2 text-gray-500 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Gift className="h-4 w-4" />
                    Rewards
                  </span>
                  <span className="text-xs flex items-center gap-1">
                    <WifiOff className="h-3 w-3" />
                    Offline
                  </span>
                </div>
              )}
              
              {/* Profile - Works Offline */}
              <Link 
                to="/profile" 
                className={`px-4 py-2 rounded transition-colors ${
                  canAccessReadingHistory 
                    ? 'text-gray-300 hover:text-white hover:bg-dark-700' 
                    : 'text-gray-500 cursor-not-allowed'
                }`}
                onClick={(e) => {
                  if (!canAccessReadingHistory) {
                    e.preventDefault();
                  } else {
                    setIsMenuOpen(false);
                  }
                }}
              >
                Profile
              </Link>
              
              <div className="border-t border-dark-700 pt-2 mt-2">
                <div className="px-4 py-2 text-sm text-gray-400">
                  Signed in as {user?.displayName || user?.nickname || user?.email}
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-gray-300 hover:text-white hover:bg-dark-700 rounded transition-colors"
                >
                  <LogOut className="h-4 w-4 inline mr-2" />
                  Logout
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>
      
      {/* Floating Feedback Collector */}
      {isAuthenticated && (
        <UserFeedbackCollector 
          trigger="floating"
          position="bottom-right"
        />
      )}
    </header>
  );
}